package br.com.texeratx.boardgametimeapp.ui.screens.games.create

import androidx.compose.animation.AnimatedContent
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.ArrowBack
import androidx.compose.material.icons.filled.Search
import androidx.compose.material3.Button
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Text
import androidx.compose.material3.TextField
import androidx.compose.material3.TopAppBar
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import br.com.texeratx.boardgametimeapp.data.model.game.GameModeDTO
import br.com.texeratx.boardgametimeapp.ui.components.CatalogSearchBottomSheet
import br.com.texeratx.boardgametimeapp.ui.components.GameThumb
import br.com.texeratx.boardgametimeapp.ui.components.IdDropdownSelector
import br.com.texeratx.boardgametimeapp.ui.components.OwnerChip
import br.com.texeratx.boardgametimeapp.ui.components.designsystem.AppScreenScaffold
import br.com.texeratx.boardgametimeapp.ui.components.designsystem.AppTopBarColors
import br.com.texeratx.boardgametimeapp.ui.screens.games.model.CatalogGameUI
import br.com.texeratx.boardgametimeapp.ui.screens.games.model.PlayerOwnerUI
import br.com.texeratx.boardgametimeapp.core.i18n.LocalStrings

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun CreateGameUI(
    state: CreateGameState,
    modes: List<GameModeDTO>,
    isLoadingOptions: Boolean,
    onValueChange: ((CreateGameState) -> CreateGameState) -> Unit,
    onOpenCatalogSearch: () -> Unit,
    onDismissCatalogSearch: () -> Unit,
    onCatalogQueryChange: (String) -> Unit,
    onCatalogPicked: (CatalogGameUI) -> Unit,
    onAddOwner: (PlayerOwnerUI) -> Unit,
    onRemoveOwner: (Long) -> Unit,
    onSave: () -> Unit,
    onBack: () -> Unit
) {
    val S = LocalStrings.current

    AppScreenScaffold(
        topBar = {
            TopAppBar(
                title = { Text(text = S.createGameTitle, fontWeight = FontWeight.Bold) },
                navigationIcon = {
                    IconButton(onClick = onBack) {
                        Icon(Icons.Default.ArrowBack, contentDescription = S.back)
                    }
                },
                colors = AppTopBarColors()
            )
        }
    ) { padding ->

        Column(
            modifier = Modifier
                .padding(padding)
                .padding(16.dp)
                .fillMaxSize()
                .verticalScroll(rememberScrollState()),
            verticalArrangement = Arrangement.spacedBy(12.dp)
        ) {

            if (!state.error.isNullOrBlank()) {
                Text(state.error!!, color = MaterialTheme.colorScheme.error)
            }

            // Catalog picker (readonly text field with trailing search icon)
            OutlinedTextField(
                value = state.catalogName,
                onValueChange = {},
                readOnly = true,
                enabled = false,
                label = { Text(S.labelCatalogGame) },
                placeholder = { Text(S.pickCatalogGame) },
                trailingIcon = {
                    IconButton(onClick = onOpenCatalogSearch) {
                        Icon(Icons.Default.Search, contentDescription = S.pickCatalogGame)
                    }
                },
                modifier = Modifier.fillMaxWidth()
            )

            if (state.catalogGameId != null) {
                state.catalogImageUrl?.let { url ->
                    GameThumb(
                        imageUrl = url,
                        contentDescription = state.catalogName,
                        modifier = Modifier
                            .fillMaxWidth()
                            .height(180.dp)
                            .clip(RoundedCornerShape(16.dp))
                    )
                }

                Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    OutlinedTextField(
                        value = state.catalogMinPlayers?.toString().orEmpty(),
                        onValueChange = {},
                        readOnly = true,
                        enabled = false,
                        label = { Text(S.labelMinPlayers) },
                        modifier = Modifier.weight(1f)
                    )
                    OutlinedTextField(
                        value = state.catalogMaxPlayers?.toString().orEmpty(),
                        onValueChange = {},
                        readOnly = true,
                        enabled = false,
                        label = { Text(S.labelMaxPlayers) },
                        modifier = Modifier.weight(1f)
                    )
                }

                OutlinedTextField(
                    value = state.catalogDuration?.toString().orEmpty(),
                    onValueChange = {},
                    readOnly = true,
                    enabled = false,
                    label = { Text(S.labelDurationMin) },
                    modifier = Modifier.fillMaxWidth()
                )

                OutlinedTextField(
                    value = state.catalogBggUrl.orEmpty(),
                    onValueChange = {},
                    readOnly = true,
                    enabled = false,
                    label = { Text(S.labelBggUrl) },
                    modifier = Modifier.fillMaxWidth()
                )

            }

            IdDropdownSelector(
                label = S.labelCategory,
                options = state.categories,
                selectedId = state.gameCategoryId,
                getId = { it.id },
                getLabel = { it.name },
                onSelect = { id -> onValueChange { it.copy(gameCategoryId = id) } }
            )

            TextField(
                value = state.manualUrl,
                onValueChange = { v -> onValueChange { it.copy(manualUrl = v) } },
                label = { Text(S.labelManualUrl) },
                modifier = Modifier.fillMaxWidth()
            )

            TextField(
                value = state.spotifyUrl,
                onValueChange = { v -> onValueChange { it.copy(spotifyUrl = v) } },
                label = { Text(S.labelSpotifyUrl) },
                placeholder = { Text("https://open.spotify.com/playlist/...") },
                supportingText = { Text(S.spotifyUrlHint) },
                modifier = Modifier.fillMaxWidth()
            )

            IdDropdownSelector(
                label = S.labelMode,
                options = modes,
                selectedId = state.gameModeId,
                getId = { it.id },
                getLabel = { it.name },
                onSelect = { id -> onValueChange { it.copy(gameModeId = id) } }
            )

            Text(
                text = S.sectionGameOwners,
                style = MaterialTheme.typography.titleMedium,
                fontWeight = FontWeight.Bold
            )

            Spacer(Modifier.height(8.dp))

            if (state.isLoadingOwners) {
                Text(S.loadingPlayers)
            } else {
                IdDropdownSelector(
                    label = S.labelAddOwner,
                    options = state.owners.filter { owner ->
                        state.selectedOwners.none { it.id == owner.id }
                    },
                    selectedId = null,
                    getId = { it.id },
                    getLabel = { it.name },
                    onSelect = { ownerId ->
                        state.owners.firstOrNull { it.id == ownerId }?.let { onAddOwner(it) }
                    }
                )
            }

            if (state.selectedOwners.isNotEmpty()) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    state.selectedOwners.forEach { owner ->
                        OwnerChip(name = owner.name, onRemove = { onRemoveOwner(owner.id) })
                    }
                }
            }

            Spacer(Modifier.height(16.dp))

            Button(
                onClick = onSave,
                modifier = Modifier.fillMaxWidth(),
                enabled = !state.isSaving
            ) {
                AnimatedContent(targetState = state.isSaving, label = "save-loading") { isSaving ->
                    if (isSaving) {
                        CircularProgressIndicator(
                            modifier = Modifier.size(20.dp),
                            strokeWidth = 2.dp,
                            color = MaterialTheme.colorScheme.onPrimary
                        )
                    } else {
                        Text(S.saveGame)
                    }
                }
            }
        }

        if (state.showCatalogSearch) {
            CatalogSearchBottomSheet(
                query = state.catalogQuery,
                results = state.catalogResults,
                isSearching = state.isSearchingCatalog,
                onQueryChange = onCatalogQueryChange,
                onPick = onCatalogPicked,
                onDismiss = onDismissCatalogSearch
            )
        }
    }
}
